import { backButton } from '@telegram-apps/sdk-react';
import { useEffect, useState } from 'react';

import { useTabbar } from '@common/providers/useTabbar';
import { safeTma } from '@common/utils/safeTma';

import styles from './Modal.module.scss';
import { BottomSheet } from '../BottomSheet';
import { Button } from '../buttons/Button';

import type { DetailedHTMLProps, HTMLAttributes, ReactNode } from 'react';

export type ModalProps = Omit<
  DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
  'data-test-id'
> & {
  testId: string;
  open?: boolean;
  onClose?: () => void;
};

export const Modal = ({
  testId,
  open,
  onClose,
  className,
  ...props
}: ModalProps) => {
  const tabbar = useTabbar();

  const [backButtonVisible, setBackButtonVisible] = useState<boolean>();
  const [tabbarBottomSheet, setTabbarBottomSheet] = useState<ReactNode>();
  const [visible, setVisible] = useState<boolean>(open || false);

  useEffect(() => {
    setVisible(open || false);
  }, [open]);

  useEffect(() => {
    if (backButtonVisible === undefined) {
      // `isVisible` is a Computed<boolean> — i.e. a FUNCTION. Passing it bare
      // to a state setter made React treat it as an updater and invoke it as
      // `isVisible(prevState)`; it only worked because the signal ignores
      // arguments and returns the boolean anyway. Call it explicitly.
      setBackButtonVisible(backButton.isVisible());
    }

    if (tabbarBottomSheet === undefined) {
      setTabbarBottomSheet(tabbar.getBottomSheet() || null);
    }

    if (visible) {
      tabbar.setBottomSheet(
        <BottomSheet
          buttons={[
            <Button testId="modal-close-button" onClick={onClose}>
              Закрыть
            </Button>,
          ]}
        />,
      );

      safeTma('modal.backButton.hide', () => backButton.hide.ifAvailable());
      document.body.style.overflow = 'hidden';
    } else {
      tabbar.setBottomSheet(tabbarBottomSheet);
      setTabbarBottomSheet(undefined);

      if (backButtonVisible) {
        safeTma('modal.backButton.show', () => backButton.show.ifAvailable());
      }
      document.body.style.overflow = 'auto';
    }

    return () => {
      // Guarded separately from the DOM reset below, so a dead Telegram
      // transport can never leave the page stuck with `overflow: hidden`.
      // Distinct label from the close branch above so Sentry can tell the
      // unmount path apart from a normal close.
      if (backButtonVisible) {
        safeTma('modal.backButton.show.unmount', () =>
          backButton.show.ifAvailable(),
        );
      }
      document.body.style.overflow = 'auto';
    };
  }, [visible]);

  return (
    <>
      {visible && (
        <div
          {...props}
          className={`${styles.modal} ${className || ''}`}
          data-test-id={`${testId}-container`}
        >
          <div className={styles.content}>{props.children}</div>
        </div>
      )}
    </>
  );
};
